import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Home, LogOut, Menu, X, Users, Mail, Shield, Tag, BarChart3, FileText, Image as ImageIcon } from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';

/**
 * Two-column admin layout: persistent sidebar + scrollable content.
 *
 * The sidebar uses NavLink so the active route gets `data-active="true"`
 * automatically — that's what we hook the orange accent off.
 */
const NAV = [
  { to: '/admin/dashboard',        label: 'Dashboard',     icon: BarChart3, testId: 'admin-nav-dashboard' },
  { to: '/admin/leads',            label: 'Leads',         icon: Users, testId: 'admin-nav-leads' },
  { to: '/admin/prices',           label: 'Preislisten',   icon: Tag,   testId: 'admin-nav-prices' },
  { to: '/admin/content',          label: 'Inhalte',       icon: FileText, testId: 'admin-nav-content' },
  { to: '/admin/media',            label: 'Medien',        icon: ImageIcon, testId: 'admin-nav-media' },
  { to: '/admin/settings/email',   label: 'E-Mail',        icon: Mail,  testId: 'admin-nav-email' },
  { to: '/admin/settings/security',label: 'Sicherheit',    icon: Shield, testId: 'admin-nav-security' },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isWorkspacePage = location.pathname === '/admin/content';
  const onLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="h-[100dvh] max-h-[100dvh] overflow-hidden bg-[#FAF8F2] text-[#1F1D1A] flex">
      <aside className="hidden md:flex md:w-56 lg:w-64 shrink-0 flex-col bg-[#1F1D1A] text-white h-full min-h-0">
        <div className="px-5 py-5 lg:px-6 lg:py-6 border-b border-white/10 shrink-0">
          <p className="font-display text-[20px] leading-none">horrenmaker</p>
          <p className="mt-1 text-[10.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-bold">Admin</p>
        </div>
        <nav className="flex-1 min-h-0 py-3 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to} to={item.to} data-testid={item.testId}
                className={({ isActive }) => `flex items-center gap-3 px-5 lg:px-6 py-2.5 text-[13.5px] transition-colors ${
                  isActive ? 'bg-white/10 text-[#F26B1A] border-l-2 border-[#F26B1A]' : 'text-white/70 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" /> {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 text-[11.5px] text-white/55 shrink-0">
          <p className="truncate" data-testid="admin-current-email">{admin?.email}</p>
          <Link
            to="/"
            data-testid="admin-shop-link"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1F1D1A] text-[11.5px] font-semibold hover:bg-[#F26B1A] hover:text-white transition-colors"
          >
            <Home className="w-3.5 h-3.5" /> Webseite
          </Link>
          <button
            type="button" onClick={onLogout}
            data-testid="admin-logout"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white text-[11.5px] font-semibold hover:bg-[#F26B1A] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile top-bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#1F1D1A] text-white flex items-center justify-between px-4 py-3 shadow-md">
        <div>
          <p className="font-display text-[15px] leading-none">horrenmaker · Admin</p>
          <p className="text-[10.5px] text-white/55 mt-0.5 truncate">{admin?.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            data-testid="admin-shop-link-mobile"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1F1D1A] text-[11px] font-semibold"
          >
            <Home className="w-3.5 h-3.5" /> Shop
          </Link>
          <button
            type="button"
            onClick={() => setMobileMenuOpen((open) => !open)}
            data-testid="admin-mobile-menu-toggle"
            aria-label={mobileMenuOpen ? 'Admin-Menue schliessen' : 'Admin-Menue oeffnen'}
            aria-expanded={mobileMenuOpen}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 text-[11px] font-semibold"
          >
            {mobileMenuOpen ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />}
          </button>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-x-3 top-[68px] z-50 rounded-2xl bg-white text-[#1F1D1A] border border-black/10 shadow-[0_24px_60px_-26px_rgba(0,0,0,0.55)] overflow-hidden">
          <nav data-testid="admin-mobile-menu" className="py-2">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`${item.testId}-mobile-menu`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-[13px] font-semibold border-b border-[#E5E0D2]/70 last:border-b-0 ${
                    isActive ? 'bg-[#F26B1A] text-white' : 'text-[#1F1D1A] hover:bg-[#FAF8F2]'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {item.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="grid grid-cols-2 gap-2 p-3 bg-[#FAF8F2] border-t border-[#E5E0D2]">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="admin-shop-link-mobile-menu"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E5E0D2] text-[#1F1D1A] text-[12px] font-semibold"
            >
              <Home className="w-3.5 h-3.5" /> Webseite
            </Link>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout-mobile-menu"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#1F1D1A] text-white text-[12px] font-semibold"
            >
              <LogOut className="w-3.5 h-3.5" /> Abmelden
            </button>
          </div>
        </div>
      )}

      <main className={`flex-1 min-w-0 h-full min-h-0 overflow-x-hidden ${
        isWorkspacePage
          ? 'overflow-hidden p-3 md:p-4 lg:p-5 pt-20 md:pt-4'
          : 'overflow-y-auto p-4 pt-20 md:pt-5 md:p-5 lg:p-6 xl:p-7'
      }`}>
        <Outlet />
      </main>
    </div>
  );
}
